import { Editor, useCurrentEditor, useEditorState } from "@tiptap/react";
import React, { useEffect, useId, useState } from "react";

import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import {
	faBold,
	faItalic,
	faLink,
	faListOl,
	faListUl,
	faUnderline,
} from "@performyard/fontawesome/pro-regular-svg-icons";

import { cn } from "../../../../lib/utils";

import { ButtonIcon } from "../../../atoms/ButtonIcon";
import { ButtonStandard } from "../../../atoms/ButtonStandard";
import { InputText } from "../../../atoms/InputText";
import { Popover, PopoverContent, PopoverTrigger } from "../../Popover";
import { Tooltip } from "../../Tooltip";

export type RichTextBarProps = {
	/**
	 * The TipTap editor instance to control. When omitted, the editor is read from the
	 * nearest TipTap EditorContext (e.g. when rendered inside the TextEditor component).
	 */
	editor?: Editor | null;
	/**
	 * Whether the bar's controls are disabled
	 */
	disabled?: boolean;
	/**
	 * Optional AI action element rendered at the end of the bar (e.g. an AI rephrase button).
	 * The AI experience is app-specific, so it is passed in as a slot rather than built in.
	 */
	aiActionElement?: React.ReactNode;
	/**
	 * The test id of the bar
	 */
	"data-testid"?: string;
};

type ToolbarButtonProps = {
	icon: IconDefinition;
	label: string;
	selected: boolean;
	disabled?: boolean;
	onClick: () => void;
	"data-testid"?: string;
};

const toolbarButtonClassName = (selected?: boolean, disabled?: boolean): string =>
	cn(
		"size-auto h-[16.2px] rounded-sm px-[4.725px] py-[2.7px] text-neutral-5",
		"[&>div]:size-auto [&_svg]:!text-[10.8px]",
		"hover:bg-transparent active:bg-transparent",
		!disabled && "hover:text-neutral-7 active:text-neutral-7",
		selected && "bg-neutral-2 text-neutral-7 hover:bg-neutral-2 active:bg-neutral-2"
	);

const ToolbarButton = ({ icon, label, selected, disabled, onClick, "data-testid": dataTestId }: ToolbarButtonProps) => (
	<Tooltip content={label} placement="top" size="small" disabled={disabled} asChild>
		<ButtonIcon
			icon={icon}
			variant="ghost"
			color="neutral"
			size={16}
			disabled={disabled}
			aria-label={label}
			aria-pressed={selected}
			data-testid={dataTestId}
			dangerouslySetClassName={toolbarButtonClassName(selected, disabled)}
			onMouseDown={(e) => e.preventDefault()}
			onClick={onClick}
		/>
	</Tooltip>
);

const Divider = () => <div aria-hidden className="h-[14px] w-px shrink-0 bg-neutral-2" />;

/**
 * Ensure a URL has a protocol so links don't resolve relative to the current page.
 */
const normalizeUrl = (url: string): string => {
	const trimmed = url.trim();
	if (!trimmed) return "";
	return /^(https?:\/\/|mailto:|tel:)/i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

/**
 * A formatting toolbar for the TipTap-based TextEditor with bold, italic, underline,
 * ordered/unordered lists, link editing, and an optional AI action slot.
 *
 * Rendered automatically by TextEditor when its `richTextBar` prop is enabled, or can be
 * used standalone by passing a TipTap `editor` instance.
 */
export const RichTextBar = ({
	editor: editorProp,
	disabled,
	aiActionElement,
	"data-testid": dataTestId,
}: RichTextBarProps) => {
	const { editor: contextEditor } = useCurrentEditor();
	const editor = editorProp ?? contextEditor;

	const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
	const [linkUrl, setLinkUrl] = useState("");
	const linkInputId = useId();

	const editorState = useEditorState({
		editor,
		selector: ({ editor }: { editor: Editor | null }) =>
			editor
				? {
						isBold: editor.isActive("bold"),
						isItalic: editor.isActive("italic"),
						isUnderline: editor.isActive("underline"),
						isOrderedList: editor.isActive("orderedList"),
						isBulletList: editor.isActive("bulletList"),
						isLink: editor.isActive("link"),
					}
				: null,
	});

	const isDisabled = disabled ?? !editor?.isEditable;

	// Close the link popover when the bar becomes disabled so it cannot re-appear with a
	// stale URL when the editor is re-enabled
	useEffect(() => {
		if (isDisabled) {
			setLinkPopoverOpen(false);
		}
	}, [isDisabled]);

	if (!editor) {
		return null;
	}

	const handleLinkOpenChange = (open: boolean) => {
		if (isDisabled) return;
		if (open) {
			setLinkUrl((editor.getAttributes("link").href as string | undefined) ?? "");
		}
		setLinkPopoverOpen(open);
	};

	const applyLink = () => {
		const href = normalizeUrl(linkUrl);

		if (!href) {
			editor.chain().focus().extendMarkRange("link").unsetLink().run();
			setLinkPopoverOpen(false);
			return;
		}

		if (editor.state.selection.empty && !editorState?.isLink) {
			editor
				.chain()
				.focus()
				.insertContent({ type: "text", text: href, marks: [{ type: "link", attrs: { href } }] })
				.run();
		} else {
			editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
		}

		editor.chain().focus().setTextSelection(editor.state.selection.to).unsetMark("link").run();

		setLinkPopoverOpen(false);
	};

	const removeLink = () => {
		editor.chain().focus().extendMarkRange("link").unsetLink().run();
		setLinkPopoverOpen(false);
	};

	return (
		<div
			role="toolbar"
			aria-label="Text formatting"
			data-testid={dataTestId}
			className="flex shrink-0 items-center gap-1 py-1 pr-2"
		>
			<ToolbarButton
				icon={faBold}
				label="Bold"
				selected={!!editorState?.isBold}
				disabled={isDisabled}
				onClick={() => editor.chain().focus().toggleBold().run()}
				data-testid={dataTestId ? `${dataTestId}-bold` : undefined}
			/>
			<Divider />
			<ToolbarButton
				icon={faItalic}
				label="Italic"
				selected={!!editorState?.isItalic}
				disabled={isDisabled}
				onClick={() => editor.chain().focus().toggleItalic().run()}
				data-testid={dataTestId ? `${dataTestId}-italic` : undefined}
			/>
			<Divider />
			<ToolbarButton
				icon={faUnderline}
				label="Underline"
				selected={!!editorState?.isUnderline}
				disabled={isDisabled}
				onClick={() => editor.chain().focus().toggleUnderline().run()}
				data-testid={dataTestId ? `${dataTestId}-underline` : undefined}
			/>
			<Divider />
			<ToolbarButton
				icon={faListOl}
				label="Ordered list"
				selected={!!editorState?.isOrderedList}
				disabled={isDisabled}
				onClick={() => editor.chain().focus().toggleOrderedList().run()}
				data-testid={dataTestId ? `${dataTestId}-ordered-list` : undefined}
			/>
			<Divider />
			<ToolbarButton
				icon={faListUl}
				label="Unordered list"
				selected={!!editorState?.isBulletList}
				disabled={isDisabled}
				onClick={() => editor.chain().focus().toggleBulletList().run()}
				data-testid={dataTestId ? `${dataTestId}-unordered-list` : undefined}
			/>
			<Divider />
			<Popover open={linkPopoverOpen} onOpenChange={handleLinkOpenChange}>
				<Tooltip content="Link" placement="top" size="small" disabled={isDisabled || linkPopoverOpen} asChild>
					<PopoverTrigger asChild>
						<ButtonIcon
							icon={faLink}
							variant="ghost"
							color="neutral"
							size={16}
							disabled={isDisabled}
							aria-label="Link"
							aria-pressed={!!editorState?.isLink}
							data-testid={dataTestId ? `${dataTestId}-link` : undefined}
							dangerouslySetClassName={toolbarButtonClassName(
								editorState?.isLink || linkPopoverOpen,
								isDisabled
							)}
							onMouseDown={(e) => e.preventDefault()}
						/>
					</PopoverTrigger>
				</Tooltip>
				<PopoverContent
					align="start"
					className="flex w-80 flex-col gap-2 p-2"
					onCloseAutoFocus={(e) => {
						e.preventDefault();
						editor.commands.focus();
					}}
				>
					<div className="flex items-center gap-2">
						<InputText
							id={linkInputId}
							aria-label="Link URL"
							placeholder="Paste or type a link..."
							value={linkUrl}
							onChange={setLinkUrl}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									applyLink();
								}
							}}
							dangerouslySetClassName="min-w-0 flex-[2]"
							data-testid={dataTestId ? `${dataTestId}-link-input` : undefined}
						/>
						<ButtonStandard
							size="medium"
							color="primary"
							onClick={applyLink}
							dangerouslySetClassName="min-w-0 flex-[1]"
						>
							Apply
						</ButtonStandard>
					</div>
					{editorState?.isLink && (
						<ButtonStandard size="medium" color="neutral" variant="outline" onClick={removeLink}>
							Remove
						</ButtonStandard>
					)}
				</PopoverContent>
			</Popover>
			{aiActionElement && (
				<>
					<Divider />
					<div className="flex items-center [&_button]:size-auto [&_button]:h-[16.2px] [&_button]:rounded-sm [&_button]:px-[4.725px] [&_button]:py-[2.7px] [&_button]:hover:bg-transparent [&_button]:active:bg-transparent [&_button>div]:size-auto [&_svg]:!text-[10.8px]">
						{aiActionElement}
					</div>
				</>
			)}
		</div>
	);
};

RichTextBar.displayName = "RichTextBar";
